<template>
  <div id="branch-page" class="bg-gray-20 pb-6 sm:pt-6 sm:pb-10 text-base">
    <div class="container mx-auto">
      <div class="hero relative sm:rounded overflow-hidden">
        <nd-img :src="`fn:${branch?.cover_image}`" class="w-full object-cover aspect-1.8 sm:aspect-unset sm:h-300px" />
        <div class="mask absolute top-0 left-0 w-full h-full text-white p-5 flex flex-col justify-end gap-1">
          <div class="branch-name flex items-center gap-1">
            <h2 class="text-2xl">{{ branch?.name }}</h2>
            <favorite-button v-if="!app.isMobile" :id="branch?.bid!" :is-favorited="branch?.is_favorite || false" type="branch" />
          </div>
          <div v-if="isDistanceShown" class="distance">{{ $t('distance_from_you', { distance_from_you: branch?.distance }) }}</div>
          <favorite-button
            v-if="app.isMobile"
            :id="branch?.bid!"
            :is-favorited="branch?.is_favorite || false"
            class="absolute top-2 right-2"
            type="branch"
          />
        </div>
      </div>
      <div class="main-content flex flex-col gap-2 mt-2 md:flex-row md:gap-6 md:mt-6">
        <div class="left-side flex-1">
          <div class="branch-info card !px-0">
            <h3 class="title px-5">{{ $t('review_merchant_info') }}</h3>
            <div class="address py-4 px-5 flex justify-between items-center gap-2">
              <span>{{ fullAddress }}</span>
              <map-button :target="googleMapAddressQueryString" :latitude="branch?.lat" :longitude="branch?.lng" />
            </div>
            <hr class="md:mx-5" />
            <template v-if="branch?.opening_hours">
              <nd-collapse class="opening-hour" :list="addressCollapseData" content-class="whitespace-pre-wrap"></nd-collapse>
            </template>
            <hr v-if="branch?.opening_hours && branch?.intro" class="md:mx-5" />
            <template v-if="branch?.intro">
              <h4 class="text-lg py-4 px-5">{{ $t('branch_characters_title') }}</h4>
              <collapsible-article class="text-article my-4" :show-expand-btn="true" :expand-btn-text="$t('branch_show_more_intro')">
                <div class="px-5 whitespace-pre-wrap" v-html="branch?.intro"></div>
              </collapsible-article>
            </template>
          </div>
        </div>

        <div class="right-side w-full md:w-386px xl:w-712px">
          <div class="branch-product-list card">
            <h3 class="title">{{ $t('branch_popular_products_title') }}</h3>
            <div class="product-grid grid grid-cols-1 xl:grid-cols-2 gap-5 py-4">
              <product-card v-for="productId in displayedBranchProducts" :key="productId" :productId="productId" class="w-full" />
            </div>
            <div v-if="isLoadMoreProductsBtnShown" class="flex justify-center mt-4">
              <nd-button v-if="!isDisplayAllProducts" type="text" class="active" @click="isDisplayAllProducts = true">
                {{ $t('branch_show_more_products') }}
              </nd-button>
              <nd-button v-else type="text" class="active" @click="isDisplayAllProducts = false">{{ $t('branch_show_less_products') }}</nd-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from '@/store/app'
import { useProductStore } from '@/store/product'
import { useBranchStore } from '@/store/branch'

import { getProducts } from '@/service/product'
import { getBranch } from '@/service/branch'

import FavoriteButton from '@/components/common/favorite-button.vue'
import MapButton from '@/components/branch/branch-detail/map-button.vue'
import ProductCard from '@/components/product/product-card.vue'
import CollapsibleArticle from '@/components/common/collapsible-article.vue'

import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import { SortRuleEnum } from '@/types/product'
import type { BranchDetailData } from '@/types/api'
import { GtmEvent } from '@/types/gtm'

definePageMeta({
  middleware: ['check-region'],
})

const { t } = useI18n()

const route = useRoute()
const app = useAppStore()
const productStore = useProductStore()
const branchStore = useBranchStore()

const branchId = computed(() => route.params.bid as string)
const { regionId } = useRegion()

const branchProducts = computed(() => productStore.listIds.branchHotProduct)
const branch = computed(() => branchStore.branchMap[Number(branchId.value)])
const branchQueryParams = computed(() => ({
  sale_region_id: regionId.value,
  ...(app.geolocation.locatedAt
    ? {
        lat: app.geolocation.latitude,
        lng: app.geolocation.longitude,
      }
    : {}),
}))

const branchRef = computed<BranchDetailData | undefined>(() => branch.value)
useNicedaySeoSetting('branch', branchRef)
const { fullAddress, googleMapAddressQueryString } = useAddress(branchRef)

const { error } = await useDualAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.BRANCH_INFO, [branchId.value, JSON.stringify(branchQueryParams.value)]),
  async () => {
    const { data } = await getBranch(branchId.value, branchQueryParams.value)

    branchStore.setToBranchMap(data)

    return data
  },
  { watch: [branchId, branchQueryParams] }
)

if (error.value) {
  // @TODO: 目前先一律到 404，未來有需要時再各別分 error code 處理。
  throwNotFoundError()
}

await useDualAsyncData(
  `branch_${branchId.value}_products`,
  async () => {
    if (!regionId.value) return []

    const { data: branchProducts } = await getProducts({
      filter_regionid: regionId.value,
      filter_bid: `${branchId.value || ''}`,
      sortby: SortRuleEnum.POPULAR,
      size: 9999,
    })

    productStore.handleProducts('branchHotProduct', branchProducts)

    return branchProducts
  },
  { watch: [branchId] }
)
const isDisplayAllProducts = ref(false)
const isLoadMoreProductsBtnShown = computed(() => {
  if (!branchProducts.value) return false
  return branchProducts.value.length > 8
})
const isDistanceShown = computed(() => {
  if (!branch.value?.distance) return false
  return parseFloat(branch.value.distance || '') <= 10
})

const displayedBranchProducts = computed(() => {
  if (!branchProducts.value) return []
  if (isDisplayAllProducts.value) return branchProducts.value
  return branchProducts.value.slice(0, 8)
})

const addressCollapseData = computed(() => {
  if (!branch.value) return []

  return [
    {
      title: t('branch_business_hours'),
      content: branch.value.opening_hours,
    },
  ]
})

onMounted(() => {
  useNuxtApp().$trackEvent(GtmEvent.BRANCH_PAGE_VIEWED)
})
</script>

<style lang="scss">
#branch-page {
  min-height: var(--content-min-height);
  .hero {
    .mask {
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.4) 100%);
    }
  }
  .card {
    @apply bg-white sm:rounded py-4 px-5;
  }
  .title {
    @apply text-xl font-500 py-3;
  }
}
</style>
